import type { ComponentType } from 'react';

/**
 * Shared glyph props. Every lucide-react icon accepts a superset of these, so
 * lucide icons AND our hand-authored SVG glyphs (see ui/svg/markup) are
 * interchangeable as an `IconComponent`. This is why Icon takes a glyph
 * component rather than a name from a central registry: each tool/module
 * supplies its own glyph, and nothing has to be kept in sync.
 */
export interface IconGlyphProps {
  size?: number | string;
  strokeWidth?: number | string;
  color?: string;
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
}

export type IconComponent = ComponentType<IconGlyphProps>;

/** The two icon sizes (D5): 16 inline (rows, fields, menus), 20 in toolbars. */
export type IconSize = 16 | 20;

interface IconProps {
  icon: IconComponent;
  size?: IconSize;
  className?: string;
}

/**
 * Standardizes size/stroke for any glyph component: one 1.5 px non-scaling stroke (D5; the `.pmt-icon` rule in
 * styles/base.css keeps it 1.5 px at either size). Decorative by default
 * (aria-hidden); accessible labeling belongs on the interactive wrapper
 * (e.g. IconButton's `label`).
 */
export function Icon({ icon: Glyph, size = 20, className }: IconProps) {
  return (
    <Glyph
      size={size}
      strokeWidth={1.5}
      className={['pmt-icon', className].filter(Boolean).join(' ')}
      aria-hidden="true"
    />
  );
}
